<template>
  <div>
    <h1 class="font-[700] text-3xl text-center text-title mb-[1vh]">
      {{ $t('pages.login.step2.twoFactor.title') }}
    </h1>
    <p class="font-[600] text-base text-center align-middle text-body mb-[2vh]">
      {{ $t('pages.login.step2.twoFactor.subtitle') }}
    </p>
    <UForm :state="twoFactorState" :schema="twoFactorSchema" @submit="onTwoFactorSubmit" class="space-y-4">
      <UFormField :label="$t('pages.login.step2.twoFactor.codeLabel')" name="code">
        <UInput 
          class="w-full text-center text-lg" 
          size="xl" 
          v-model="twoFactorState.code" 
          :placeholder="$t('pages.login.step2.twoFactor.codePlaceholder')"
          maxlength="6"
          :disabled="isLoading" 
          :ui="{ base: 'py-3 px-4' }" 
        />
      </UFormField>

      <UButton type="submit" color="primary" size="xl" class="py-3" block :loading="isLoading">
        {{ $t('pages.login.step2.twoFactor.verify') }}
      </UButton>

      <UButton 
        type="button" 
        color="neutral" 
        variant="outline" 
        size="xl" 
        class="py-3" 
        block 
        @click="$emit('back-to-login')"
        :disabled="isLoading"
      >
        {{ $t('pages.login.step2.twoFactor.backToLogin') }}
      </UButton>
    </UForm>
  </div>
</template>

<script setup lang="ts">
import type { FormSubmitEvent } from '@nuxt/ui';
import * as yup from 'yup';
import { login2FAApi } from '~/api/auth';
import { useDeviceId } from '~/composables/useDeviceId';
import { useToastStore } from '~/stores/toast';
import type { ApiErrorWithStatus } from '~/types/api';

interface Props {
  provisionalToken: string;
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'verification-success': [token: string, refreshToken: string];
  'back-to-login': [];
}>();

const { t } = useI18n();
const { getOrCreateDeviceId } = useDeviceId();
const toastStore = useToastStore();
const isLoading = ref(false);

const twoFactorState = ref({
  code: '',
});

const twoFactorSchema = yup.object({
  code: yup
    .string()
    .required(t('pages.login.step2.twoFactor.invalidCode'))
    .matches(/^\d{6}$/, t('pages.login.step2.twoFactor.invalidCode')),
});

async function onTwoFactorSubmit(event: FormSubmitEvent<typeof twoFactorState.value>) {
  if (isLoading.value) return;

  try {
    isLoading.value = true;
    const deviceId = getOrCreateDeviceId();
    const response = await login2FAApi({
      token: props.provisionalToken,
      code: event.data.code,
      device_id: deviceId,
    });

    if (response) {
      emit('verification-success', response.token, response.refresh_token);
    } else {
      toastStore.error(t('pages.login.step2.twoFactor.verificationFailed'));
    }
  } catch (error) {
    const apiError = error as ApiErrorWithStatus | Error;
    if ('message' in apiError && apiError.message) {
      toastStore.error(apiError.message);
    } else {
      toastStore.error(t('pages.login.step2.twoFactor.verificationFailed'));
    }
  } finally {
    isLoading.value = false;
  }
}

// Reset code when component is used
watch(
  () => props.provisionalToken,
  () => {
    twoFactorState.value.code = '';
  }
);
</script> 